import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { get, post, put, del } from '../../api/base';
import { NodeType } from '../../types/types';

interface NodeState {
  node: NodeType;
  nodes: NodeType[];
  loading: boolean;
  error: any;
}

export const initialState: NodeState = {
  node: {
    id: null,
    name: '',
    project: {
      id: null,
      name: '',
      company: null,
      created_by: null,
      customer: '',
      business_unit: '',
      facility: '',
      description: '',
    },
    description: '',
    design_intention: '',
    special_consideration: '',
    comments: '',
    color: '',
  },
  nodes: [],
  loading: false,
  error: null,
};

export const getAllNodesByProjectId = createAsyncThunk(
  'nodes/getAllNodesByProjectId',
  async (projectId: number, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await get(`project/${projectId}/nodes/`, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getNode = createAsyncThunk('nodes/getNode', async (node: NodeType, { rejectWithValue, getState }) => {
  try {
    const state: any = getState();
    const headers = {
      Authorization: `Bearer ${state.user.access}`,
    };
    const response = await get(`project/${node?.project?.id}/nodes/${node.id}/`, {
      headers: headers,
    });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data);
  }
});

export const createNode = createAsyncThunk(
  'nodes/createNode',
  async (node: NodeType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await post(`project/${node?.project?.id}/nodes/`, node, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const cloneNode = createAsyncThunk(
  'nodes/cloneNode',
  async (
    { projectId, nodeId }: { projectId: number; nodeId: number },
    { rejectWithValue, getState }
  ) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await post(`project/${projectId}/nodes/${nodeId}/clone/`, {}, { headers });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateNode = createAsyncThunk(
  'nodes/updateNode',
  async (node: NodeType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await put(`project/${node?.project?.id}/nodes/${node.id}/`, node, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteNode = createAsyncThunk(
  'nodes/deleteNode',
  async (node: NodeType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await del(`project/${node?.project?.id}/nodes/${node.id}/`, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

// nodesSlice thunks – mover arriba/abajo
export const moveNodeUp = createAsyncThunk(
  'nodes/moveNodeUp',
  async (nodeId: number, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = { Authorization: `Bearer ${state.user.access}` };
      const response = await post(`node/${nodeId}/up/`, {}, { headers });
      // backend responde { id, order }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data ?? error);
    }
  }
);

export const moveNodeDown = createAsyncThunk(
  'nodes/moveNodeDown',
  async (nodeId: number, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = { Authorization: `Bearer ${state.user.access}` };
      const response = await post(`node/${nodeId}/down/`, {}, { headers });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data ?? error);
    }
  }
);

const nodeSlice = createSlice({
  name: 'nodes',
  initialState,
  reducers: {
    clearNode: (state) => {
      state.node = initialState.node;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllNodesByProjectId.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllNodesByProjectId.fulfilled, (state, action) => {
        state.nodes = action.payload;
        state.loading = false;
      })
      .addCase(getAllNodesByProjectId.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(getNode.pending, (state) => {
        state.loading = true;
      })
      .addCase(getNode.fulfilled, (state, action) => {
        state.node = action.payload;
        state.loading = false;
      })
      .addCase(getNode.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(createNode.pending, (state) => {
        state.loading = true;
      })
      .addCase(createNode.fulfilled, (state, action) => {
        state.node = action.payload;
        state.loading = false;
      })
      .addCase(createNode.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(updateNode.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateNode.fulfilled, (state, action) => {
        state.node = action.payload;
        state.loading = false;
      })
      .addCase(updateNode.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(deleteNode.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteNode.fulfilled, (state, action) => {
        state.node = initialState.node;
        state.loading = false;
      })
      .addCase(deleteNode.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(cloneNode.pending, (state) => {
        state.loading = true;
      })
      .addCase(cloneNode.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(cloneNode.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(moveNodeUp.pending, (state) => {
        state.loading = true;
      })
      .addCase(moveNodeUp.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(moveNodeUp.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(moveNodeDown.pending, (state) => {
        state.loading = true;
      })
      .addCase(moveNodeDown.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(moveNodeDown.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      });
  },
});

export const { clearNode } = nodeSlice.actions;

export default nodeSlice.reducer;
